<template>
  <button
    v-if="session !== null"
    type="button"
    class="trigger inline-flex items-center gap-2 rounded-full border border-transparent px-2 py-1 transition-colors duration-200 hover:border-border-strong hover:bg-bg-surface focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
    :aria-expanded="open"
    aria-haspopup="dialog"
    @click="onClick"
  >
    <img
      v-if="avatarUrl && !avatarFailed"
      :src="avatarUrl"
      :alt="avatarAlt"
      class="h-7 w-7 rounded-full border border-border bg-bg-surface"
      referrerpolicy="no-referrer"
      @error="onAvatarError"
    />
    <span
      v-else
      class="flex h-7 w-7 items-center justify-center rounded-full border border-accent/40 bg-accent/20 text-xs font-semibold text-accent"
    >{{ avatarInitial }}</span>
    <span class="hidden max-w-28 truncate text-xs font-medium text-text-primary sm:inline">{{ displayName }}</span>
  </button>
  <AppButton
    v-else
    type="button"
    :variant="ButtonVariant.Primary"
    :size="ButtonSize.Sm"
    @click="onLogin"
  >{{ messages.profileChip.login }}</AppButton>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useDiscordAvatar } from '@/shared/composables'
import { AppButton, ButtonSize, ButtonVariant } from '@/shared/ui'
import { useGuild } from '@/shared/ports/guild'
import { loginWithDiscord, type SessionUser } from '@/app/session'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { appShellEn } from './locales/en.ts'
import { appShellRu } from './locales/ru.ts'

export interface ProfileChipProps {
  readonly session: SessionUser | null
  readonly open: boolean
}

defineProps<ProfileChipProps>()

const emit = defineEmits<{
  (e: 'toggle'): void
  (e: 'logout'): void
}>()

const { avatarUrl, avatarFailed, onAvatarError, displayName, avatarInitial } =
  useDiscordAvatar({ size: 64 })

const guild = useGuild()
const catalog = defineLocaleCatalog({ en: appShellEn, ru: appShellRu })
const messages = useLocaleMessages(catalog)

const avatarAlt = computed<string>(() => messages.value.profileChip.avatarAlt(displayName.value))

function onClick(): void {
  emit('toggle')
}

/**
 * Kick off Discord OAuth2. The active snowflake comes from the guild port
 * (initially seeded from `VITE_GUILD_ID`, and possibly overwritten at runtime
 * by the server-selector dropdown). It is login context for a target-role
 * lookup only; the callback discovers the account's accessible guilds from
 * Discord rather than requiring membership in this pre-login selection.
 */
function onLogin(): void {
  loginWithDiscord(guild.activeDiscordGuildId.value)
}
</script>
